import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectRoofTypes, selectRoofConditions, selectReroofStatuses } from '../../../store/selectors/databaseSelectors';
import { addRoofType, addRoofCondition, addReroofStatus } from '../../../store/slices/databaseSlice';
import { roofTypesService, roofConditionsService, reroofStatusService } from '../../../services';
import { RoofType, RoofCondition, ReroofStatus } from '../../../types';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG } from '../../../constants';
import toast from 'react-hot-toast';

interface RoofForm {
  value: string;
}

type RoofTab = 'types' | 'conditions' | 'reroof_status';

const ROOF_TABS: { id: RoofTab; label: string }[] = [
  { id: 'types', label: 'Roof Types' },
  { id: 'conditions', label: 'Roof Conditions' },
  { id: 'reroof_status', label: 'Reroof Status' }
];

export const RoofManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  
  // Get data from Redux
  const roofTypes = useAppSelector(selectRoofTypes);
  const roofConditions = useAppSelector(selectRoofConditions);
  const reroofStatuses = useAppSelector(selectReroofStatuses);
  
  // Form states
  const [roofForm, setRoofForm] = useState<RoofForm>({
    value: ''
  });
  
  // UI states
  const [activeTab, setActiveTab] = useState<RoofTab>('types');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localRoofData, setLocalRoofData] = useState({
    types: roofTypes,
    conditions: roofConditions,
    reroof_status: reroofStatuses
  });

  // Initialize local state with Redux data
  useEffect(() => {
    setLocalRoofData({
      types: roofTypes,
      conditions: roofConditions,
      reroof_status: reroofStatuses
    });
  }, [roofTypes, roofConditions, reroofStatuses]);

  // Get current data for active tab
  const getCurrentData = (): (RoofType | RoofCondition | ReroofStatus)[] => {
    switch (activeTab) {
      case 'types': return localRoofData.types;
      case 'conditions': return localRoofData.conditions;
      case 'reroof_status': return localRoofData.reroof_status;
      default: return [];
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roofForm.value.trim()) {
      toast('Please enter a value', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      let newItem: RoofType | RoofCondition | ReroofStatus;
      
      switch (activeTab) {
        case 'types':
          newItem = await roofTypesService.createRoofType({
            type: roofForm.value.trim()
          });
          dispatch(addRoofType(newItem as RoofType));
          setLocalRoofData(prev => ({ ...prev, types: [newItem as RoofType, ...prev.types] }));
          break;
          
        case 'conditions':
          newItem = await roofConditionsService.createRoofCondition({
            value: roofForm.value.trim()
          });
          dispatch(addRoofCondition(newItem as RoofCondition));
          setLocalRoofData(prev => ({ ...prev, conditions: [newItem as RoofCondition, ...prev.conditions] }));
          break;
          
        case 'reroof_status':
          newItem = await reroofStatusService.createReroofStatus({
            value: roofForm.value.trim()
          });
          dispatch(addReroofStatus(newItem as ReroofStatus));
          setLocalRoofData(prev => ({ ...prev, reroof_status: [newItem as ReroofStatus, ...prev.reroof_status] }));
          break;
      }
      
      // Reset form
      setRoofForm({ value: '' });
      
      toast.success(`${ROOF_TABS.find(t => t.id === activeTab)?.label?.slice(0, -1)} created successfully!`, TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating roof item:', error);
      toast.error('Failed to create roof item. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentData = getCurrentData();

  const getPlaceholder = () => {
    switch (activeTab) {
      case 'types': return 'e.g., Asphalt Shingle, Tile, Metal';
      case 'conditions': return 'e.g., Good, Fair, Poor';
      case 'reroof_status': return 'e.g., No Reroof, Partial Reroof, Full Reroof';
      default: return 'Enter value';
    }
  };

  const getDisplayValue = (item: RoofType | RoofCondition | ReroofStatus) => {
    if ('type' in item) return (item as RoofType).type;
    return (item as RoofCondition | ReroofStatus).value;
  };

  return (
    <div className="p-4">
      {/* Tabs */}
      <div className="flex flex-wrap border-b border-slate-200 mb-4">
        {ROOF_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 font-medium text-sm border-b-2 transition-colors duration-200 mr-4 mb-2 ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label} ({getCurrentData().length})
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Form Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">
            Add New {ROOF_TABS.find(t => t.id === activeTab)?.label?.slice(0, -1)}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                {activeTab === 'types' ? 'Type *' : 'Value *'}
              </label>
              <input
                type="text"
                value={roofForm.value}
                onChange={(e) => setRoofForm(prev => ({ ...prev, value: e.target.value }))}
                placeholder={getPlaceholder()}
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting || !roofForm.value.trim()}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? 'Creating...' : `Create ${ROOF_TABS.find(t => t.id === activeTab)?.label?.slice(0, -1)}`}
            </button>
          </form>
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">
              Existing {ROOF_TABS.find(t => t.id === activeTab)?.label}
            </h3>
            <span className="text-xs text-slate-500">
              {currentData.length} items
            </span>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {currentData.length > 0 ? (
              <div className="flex flex-wrap gap-2 p-2">
                {currentData.map((item) => (
                  <div key={item.id} className="inline-flex items-center px-3 py-1 bg-slate-50 text-slate-800 rounded-full border border-slate-200 hover:bg-slate-100 transition-colors">
                    <span className="text-xs font-medium">{getDisplayValue(item)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-xs text-center py-8">
                No {ROOF_TABS.find(t => t.id === activeTab)?.label?.toLowerCase()} found
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};